/**
 * PayoffDiagram — a small, self-contained SVG payoff-at-expiry chart for the App
 * Guide's worked options examples. Given a set of legs (action / type / strike /
 * premium, in the SAME per-unit ₹ as the example), it computes the P&L-at-expiry
 * curve, shades profit (green) / loss (red), marks the current spot, break-evens
 * and max-profit / max-loss — so readers see the "shape" of each structure with the
 * exact numbers used in the surrounding text.
 *
 * Illustrative only — real app payoffs come from the live chain via the builder;
 * these use round premiums to teach the shape.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';

export interface PayoffLeg {
  action: 'BUY' | 'SELL';
  type: 'CE' | 'PE';
  strike: number;
  premium: number;
}

interface Props {
  legs: PayoffLeg[];
  spot: number;
  /** Optional explicit x-axis window; else derived from strikes + spot. */
  domain?: [number, number];
  height?: number;
  caption?: string;
}

const intrinsic = (type: 'CE' | 'PE', strike: number, s: number) =>
  type === 'CE' ? Math.max(s - strike, 0) : Math.max(strike - s, 0);

const pnlAt = (legs: PayoffLeg[], s: number) =>
  legs.reduce((sum, l) => {
    const intr = intrinsic(l.type, l.strike, s);
    return sum + (l.action === 'BUY' ? intr - l.premium : l.premium - intr);
  }, 0);

const fmt = (v: number) => Math.round(v).toLocaleString('en-IN');
const fmtSigned = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;

const PayoffDiagram: React.FC<Props> = ({ legs, spot, domain, height = 210, caption }) => {
  const { C, isDark } = useAppTheme();
  const green = C.chartBull || '#26a69a';
  const red = C.chartBear || '#ef5350';
  const grid = C.chartGrid || (isDark ? '#37474f' : '#cfd8dc');
  const spotColor = C.spot || '#ffb74d';

  const W = 560, H = height, padL = 54, padR = 16, padT = 16, padB = 34;
  const plotW = W - padL - padR, plotH = H - padT - padB;

  const strikes = legs.map(l => l.strike);
  const [dMin, dMax] = domain || (() => {
    const lo = Math.min(...strikes, spot), hi = Math.max(...strikes, spot);
    const pad = Math.max((hi - lo) * 0.45, hi * 0.03);
    return [lo - pad, hi + pad] as [number, number];
  })();

  const N = 121;
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < N; i++) { const s = dMin + (dMax - dMin) * (i / (N - 1)); xs.push(s); ys.push(pnlAt(legs, s)); }
  const yMax = Math.max(...ys, 0), yMin = Math.min(...ys, 0);
  const ySpan = (yMax - yMin) || 1;
  const yPad = ySpan * 0.12;
  const yHi = yMax + yPad, yLo = yMin - yPad;

  const toX = (s: number) => padL + ((s - dMin) / (dMax - dMin)) * plotW;
  const toY = (p: number) => padT + (1 - (p - yLo) / (yHi - yLo)) * plotH;
  const zeroY = toY(0);

  // Profit / loss shaded polygons (payoff clamped above / below zero, back along zero).
  const areaPath = (clampAbove: boolean) => {
    let d = `M ${toX(xs[0])} ${zeroY}`;
    for (let i = 0; i < N; i++) { const y = clampAbove ? Math.max(ys[i], 0) : Math.min(ys[i], 0); d += ` L ${toX(xs[i]).toFixed(1)} ${toY(y).toFixed(1)}`; }
    d += ` L ${toX(xs[N - 1])} ${zeroY} Z`;
    return d;
  };
  const linePath = xs.map((s, i) => `${i ? 'L' : 'M'} ${toX(s).toFixed(1)} ${toY(ys[i]).toFixed(1)}`).join(' ');

  // Break-evens (payoff sign change) via linear interpolation.
  const bes: number[] = [];
  for (let i = 1; i < N; i++) {
    if ((ys[i - 1] <= 0 && ys[i] > 0) || (ys[i - 1] >= 0 && ys[i] < 0)) {
      const t = ys[i - 1] / (ys[i - 1] - ys[i]);
      bes.push(xs[i - 1] + t * (xs[i] - xs[i - 1]));
    }
  }

  const yTicks = [yHi, (yHi + yLo) / 2, 0, yLo].filter((v, i, a) => a.indexOf(v) === i);

  return (
    <Box sx={{ my: 1.5 }}>
      <Box component="svg" viewBox={`0 0 ${W} ${H}`} sx={{ width: '100%', maxWidth: 560, display: 'block' }} role="img" aria-label="Option payoff at expiry">
        {/* y grid + labels */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={toY(v)} y2={toY(v)} stroke={grid} strokeWidth={v === 0 ? 1.4 : 0.6} opacity={v === 0 ? 0.9 : 0.4} />
            <text x={padL - 6} y={toY(v) + 3} textAnchor="end" fontSize={9} fill={'#90a4ae'}>{v >= 0 ? '' : '−'}₹{fmt(Math.abs(v))}</text>
          </g>
        ))}
        {/* profit / loss shading */}
        <path d={areaPath(true)} fill={green} opacity={0.18} />
        <path d={areaPath(false)} fill={red} opacity={0.18} />
        {/* strikes */}
        {strikes.map((k, i) => (
          <line key={i} x1={toX(k)} x2={toX(k)} y1={padT} y2={padT + plotH} stroke={grid} strokeDasharray="2 4" opacity={0.5} />
        ))}
        {/* payoff line */}
        <path d={linePath} fill="none" stroke={green} strokeWidth={2.2} />
        {/* spot */}
        <line x1={toX(spot)} x2={toX(spot)} y1={padT} y2={padT + plotH} stroke={spotColor} strokeWidth={1.6} />
        <text x={toX(spot)} y={padT + 9} textAnchor="middle" fontSize={9} fontWeight={700} fill={spotColor}>spot {fmt(spot)}</text>
        {/* break-evens */}
        {bes.map((b, i) => (
          <g key={i}>
            <circle cx={toX(b)} cy={zeroY} r={3} fill={C.accent || '#ab47bc'} />
            <text x={toX(b)} y={zeroY + 14} textAnchor="middle" fontSize={8.5} fontWeight={700} fill={C.accent || '#ab47bc'}>BE {fmt(b)}</text>
          </g>
        ))}
        {/* x labels at strikes */}
        {strikes.filter((v, i, a) => a.indexOf(v) === i).map((k, i) => (
          <text key={i} x={toX(k)} y={H - 6} textAnchor="middle" fontSize={8.5} fill={'#90a4ae'}>{fmt(k)}</text>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.25, px: 0.5 }}>
        <Typography variant="caption" sx={{ color: green, fontWeight: 700 }}>Max profit {fmtSigned(yMax)}</Typography>
        <Typography variant="caption" sx={{ color: red, fontWeight: 700 }}>Max loss {yMin < -1e5 ? '(large / undefined)' : fmtSigned(yMin)}</Typography>
        {bes.length > 0 && <Typography variant="caption" sx={{ color: 'text.secondary' }}>Break-even {bes.map(b => fmt(b)).join(' / ')}</Typography>}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>(per 1 unit, at expiry)</Typography>
      </Box>
      {caption && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, mt: 0.25 }}>{caption}</Typography>}
    </Box>
  );
};

export default PayoffDiagram;
